import { apiFetch } from '../../services/apiClient.js'
import { getAppConfig } from '../../config/appConfig.js'

/** Hard cap on the platform logout POST so an exit the user asked for always completes. */
const LOGOUT_TIMEOUT_MS = 5000

/**
 * Where to send someone whose session has ended. Re-authentication is platform-owned;
 * `/login` is deliberately not used as a fallback because it is not a route in this
 * SPA and resolves to the not-found page.
 */
export function sessionEndTarget(): string {
  return getAppConfig().platformLogoutUrl || '/'
}

/**
 * The platform login, carrying the page the caller was trying to reach so authentication
 * returns there instead of a generic landing page. Null when no login is configured, so
 * the caller can keep showing the session-expired notice rather than navigate nowhere.
 *
 * Whether the destination is an acceptable redirect target is the platform's call, not
 * ours — its validator already allowlists origins and blocks open redirects, and a second
 * check here would be a weaker copy of it.
 */
export function loginUrlWithReturn(returnTo: string): string | null {
  const loginUrl = getAppConfig().platformLoginUrl
  if (!loginUrl) return null
  try {
    const url = new URL(loginUrl)
    url.searchParams.set('redirect_uri', returnTo)
    return url.toString()
  } catch {
    return null
  }
}

export async function logout(): Promise<{ url: string } | null> {
  const platformLogoutUrl = getAppConfig().platformLogoutUrl
  if (!platformLogoutUrl) return null
  const controller = new AbortController()
  const timer = window.setTimeout(() => controller.abort(), LOGOUT_TIMEOUT_MS)
  try {
    const res = await apiFetch(platformLogoutUrl, { method: 'POST', signal: controller.signal })
    if (!res.ok) throw new Error(`Logout failed: ${res.status}`)
    return (await res.json()) as { url: string }
  } finally {
    window.clearTimeout(timer)
  }
}
